import React from 'react';
import { Car, Shield, Clock, DollarSign, Users, Plane } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export const Services = () => {
  const services = [
    {
      icon: Plane,
      title: 'Airport Transfers',
      description: 'Reliable transportation to and from O\'Hare International Airport. We monitor flight schedules to ensure timely pickups.',
      features: ['Flight tracking', 'Meet & greet service', 'Luggage assistance', 'O\'Hare Airport (ORD)']
    },
    {
      icon: DollarSign,
      title: 'Flat-Rate Pricing',
      description: 'Transparent pricing with no hidden fees. Know your cost before you book.',
      features: ['No surprises', 'City-based rates', 'All-inclusive pricing', 'Multiple payment options']
    },
    {
      icon: Shield,
      title: 'Insured & Licensed',
      description: 'Fully licensed and insured service with professional, background-checked drivers.',
      features: ['Full insurance', 'Licensed drivers', 'Clean vehicles', 'Safety first']
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">Our Services</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Premium airport transportation services tailored to your needs. From executive sedans to spacious SUVs, we've got you covered.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  className="group hover:shadow-2xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-2"
                >
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mb-6 group-hover:bg-[#00D4FF] transition-colors duration-300">
                      <Icon className="w-8 h-8 text-[#00D4FF] group-hover:text-black transition-colors duration-300" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-gray-700">
                          <div className="w-1.5 h-1.5 bg-[#00D4FF] rounded-full mr-3" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vehicle Information */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Fleet</h2>
            <div className="w-24 h-1 bg-[#00D4FF] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_livery-chicago/artifacts/ag5mq5qj_image%20%282%29.png"
                  alt="Executive Sedan"
                  className="w-full h-64 object-contain rounded-lg"
                />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Executive Sedans</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Up to 3 passengers</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Premium leather seating</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Climate-controlled comfort</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Perfect for business travel</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_livery-chicago/artifacts/ycshrpu4_image.png"
                  alt="Luxury SUV"
                  className="w-full h-64 object-contain rounded-lg"
                />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Luxury SUVs</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Up to 6 passengers</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Spacious interior</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Extra cargo space</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#00D4FF] rounded-full mr-3 mt-2" />
                  <span>Ideal for families and groups</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};